import { useState } from "react";
import { useStore } from "../lib/storeContext";
import { renameEntity } from "../lib/mutations";
import type { EntityType } from "../lib/types";

export function RenameButton({ type, id, current }: { type: EntityType; id: string; current: string }) {
  const { store, touch } = useStore();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(current);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Viewers are read-only in shared projects (the server would reject anyway).
  if (store.roleFor(store.projectIdOf(type, id)) === "viewer") return null;

  async function save() {
    const trimmed = value.trim();
    if (!trimmed || trimmed === current) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await renameEntity(store, type, id, trimmed);
      touch();
      setEditing(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed");
      touch();
    } finally {
      setBusy(false);
    }
  }

  if (!editing) {
    return (
      <button
        className="rounded-full p-1.5 text-muted hover:bg-cream hover:text-accent"
        title="Rename"
        onClick={() => {
          setValue(current);
          setEditing(true);
        }}
      >
        ✎
      </button>
    );
  }

  return (
    <span className="inline-flex items-center gap-1">
      <input
        autoFocus
        className="rounded-lg border border-black/10 px-2 py-1 text-base"
        value={value}
        disabled={busy}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") setEditing(false);
        }}
      />
      <button className="btn-warm px-3 py-1 text-sm" onClick={save} disabled={busy}>
        {busy ? "…" : "Save"}
      </button>
      <button className="btn-ghost px-3 py-1 text-sm" onClick={() => setEditing(false)} disabled={busy}>
        Cancel
      </button>
      {err && <span className="text-xs text-red-500">{err}</span>}
    </span>
  );
}
